import React, { useEffect, useState } from "react";
import { Table, Typography, Button, Card, Row, Col, Tag, Avatar } from "antd";
import {
  PieChart, Pie, Cell, Legend,
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis, Tooltip,
  ResponsiveContainer
} from "recharts";

const { Title } = Typography;

// 🎨 COLORS
const COLORS = ["#00C49F", "#FFBB28", "#FF4D4F"];

function ProjectReportList() {

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("access-token");

    fetch("https://rhrmapi.up2dateinc.com/api/projects", {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setProjects(Array.isArray(data) ? data : []))
      .catch(() => setProjects([]));
  }, []);

  // ✅ HANDLE REPORT
  const handleReport = (project) => {
    setSelectedProject(project);

    const status = project.status?.toLowerCase().trim();

    const data = [
      { name: "Completed", value: status === "completed" ? 1 : 0 },
      { name: "In Progress", value: status === "in progress" ? 1 : 0 },
      { name: "Not Started", value: status === "not started" ? 1 : 0 },
    ];

    setChartData(data);
  };

  // 🎨 STATUS COLOR
  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === "completed") return "green";
    if (s === "in progress") return "orange";
    return "red";
  };

  const columns = [
    { title: "SL No", render: (_, __, index) => index + 1 },
    { title: "Project Name", dataIndex: "projectName" },
    { title: "Client Name", dataIndex: "client" },
    { title: "Project Lead", dataIndex: "projectLead" },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      )
    },
    {
      title: "Action",
      render: (_, record) => (
        <Button type="primary" onClick={() => handleReport(record)}>
          View Report
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>

      <Title level={3}>📊 Project Reports Dashboard</Title>

      {/* TABLE */}
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={projects}
        pagination={{ pageSize: 5 }}
        style={{ marginTop: 20 }}
      />

      {/* DASHBOARD */}
      {selectedProject && (
        <div style={{ marginTop: 40 }}>

          <Title level={4}>
            🚀 {selectedProject.projectName} Dashboard
          </Title>

          {/* 🔷 SUMMARY */}
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={6}>
              <Card title="Client">{selectedProject.client}</Card>
            </Col>
            <Col span={6}>
              <Card title="Project Lead">{selectedProject.projectLead}</Card>
            </Col>
            <Col span={6}>
              <Card title="Status">
                <Tag color={getStatusColor(selectedProject.status)}>
                  {selectedProject.status}
                </Tag>
              </Card>
            </Col>
            <Col span={6}>
              <Card title="Duration">{selectedProject.duration} days</Card>
            </Col>
          </Row>

          {/* 👥 TEAM MEMBERS */}
          <Card title="👥 Team Members" style={{ marginBottom: 20 }}>
            {selectedProject.teamMembers ? (
              selectedProject.teamMembers
                .split(",") // ✅ convert string to array
                .map((member, index) => (
                  <Tag key={index} style={{ margin: 5 }}>
                    <Avatar style={{ marginRight: 5 }}>
                      {member.trim()[0]}
                    </Avatar>
                    {member.trim()}
                  </Tag>
                ))
            ) : (
              <p>No team members assigned</p>
            )}
          </Card>

          {/* 🔶 CHARTS */}
          <Row gutter={16}>

            {/* DONUT */}
            <Col span={12}>
              <Card title="Status Distribution (Donut)">
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={60}
                        outerRadius={100}
                        label
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={index} fill={COLORS[index]} />
                        ))}
                      </Pie>
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>

            {/* PIE */}
            <Col span={12}>
              <Card title="Status Overview (Pie)">
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        outerRadius={100}
                        label
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={index} fill={COLORS[index]} />
                        ))}
                      </Pie>
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>

          </Row>

          <Row gutter={16} style={{ marginTop: 20 }}>

            {/* BAR */}
            <Col span={12}>
              <Card title="Progress (Bar)">
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value">
                        {chartData.map((entry, index) => (
                          <Cell key={index} fill={COLORS[index]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>

            {/* LINE */}
            <Col span={12}>
              <Card title="Trend (Line)">
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#1890ff"
                        strokeWidth={3}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>

          </Row>

        </div>
      )}

    </div>
  );
}

export default ProjectReportList;